<template>
  <div class="flex items-center gap-3">
    <div class="relative size-16 shrink-0 overflow-hidden rounded-lg">
      <template v-if="siteLink.image_url">
        <img :src="siteLink.image_url" :alt="siteLink.site_link_title" class="size-full object-cover">
        <button
          v-if="!disabled"
          type="button"
          class="absolute inset-0 flex cursor-pointer items-center justify-center bg-black/50 text-white opacity-0 transition-opacity hover:opacity-100 focus-visible:opacity-100"
          :aria-label="tr('Remove')"
          @click="() => removeThumbnail()"
        >
          <trash-2-icon class="size-5" />
        </button>
      </template>
      <label
        v-else
        :class="[ 'glass-input flex size-full flex-col items-center justify-center gap-1 text-[11px] font-semibold text-primary', siteLink._uploading ? 'cursor-progress' : 'cursor-pointer' ]"
      >
        <input
          ref="fileInput"
          type="file"
          accept="image/jpeg, image/png, image/webp"
          class="sr-only"
          @change="onFileSelected"
        >
        <loader-circle-icon v-if="siteLink._uploading" class="size-5 animate-spin" />
        <upload-icon v-else class="size-5" />
        {{ siteLink._uploading ? tr('Uploading...') : tr('Upload') }}
      </label>
    </div>

    <div class="flex min-w-0 flex-1 flex-col gap-2">
      <ui-input
        v-model="siteLink.site_link_url"
        :placeholder="tr('https://your-site.com')"
        :disabled="disabled"
        :aria-invalid="invalidUrl || null"
        @blur="validateUrl"
      />
      <ui-input
        v-model="siteLink.site_link_title"
        :placeholder="tr('Display label')"
        :disabled="disabled"
        maxlength="25"
      />
    </div>

    <ui-button
      variant="ghost"
      size="icon"
      class="shrink-0 text-destructive hover:text-destructive"
      :aria-label="tr('Remove')"
      @click="() => $emit('remove')"
    >
      <trash-2-icon />
    </ui-button>
  </div>
</template>

<script>
import { LoaderCircle, Trash2, Upload } from '@lucide/vue';

import FileService from '@/services/File.js';
import { isValidURL } from '@/utils/validators.js';

export default {
  name: 'SiteLinkItem',
  components: {
    LoaderCircleIcon: LoaderCircle,
    Trash2Icon: Trash2,
    UploadIcon: Upload,
  },
  props: {
    siteLink: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  emits: [ 'remove' ],
  data: () => ({
    invalidUrl: null,
  }),
  methods: {
    validateUrl() {
      this.invalidUrl = null;

      if (!this.siteLink.site_link_url) {
        return;
      }

      this.invalidUrl = !isValidURL(this.siteLink.site_link_url);
    },
    async onFileSelected(event) {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$toast.error(this.tr('Image size is too big. Maximum size is :size.', { ':size': '5 MB' }));
        this.$refs.fileInput.value = null;
        return;
      }

      this.siteLink._uploading = true;

      try {
        const response = await FileService.uploadSiteLinkThumbnail(file);
        this.siteLink.image_file_id = response.id;
        this.siteLink.image_url = response.url;
      }
      catch (err) {
        this.$toast.error(this.tr('Failed to upload thumbnail image.'));
      }
      finally {
        this.siteLink._uploading = false;
        this.$refs.fileInput.value = null;
      }
    },
    removeThumbnail() {
      if (this.disabled) {
        return;
      }

      this.siteLink.image_file_id = null;
      this.siteLink.image_url = null;
    },
  },
};
</script>
